import React, { useState } from "react";
import { Row, Col, Card, Button, Modal, Tag, Image } from "antd";
import { addHttpsPrefix, replaceValueInDataSet } from "../../../../../utils/index";
import {
  DEFAULT_COMPANY_LOGO,
  IMAGE_HOST,
  INVESTOR_LOGO_HOST,
  LOGO_DEV_PUBLIC_KEY,
} from "../../../../../constant";
import {
  Close,
  OpenInNew,
  Diamond,
  LooksOne,
  LooksTwo,
  Looks3,
} from "@mui/icons-material";

const normalizeTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags))
    return tags
      .filter(Boolean)
      .map((t) => (typeof t === "string" ? t.trim() : String(t)));
  if (typeof tags === "string")
    return tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  return [];
};

const toTitleCaseWords = (value) =>
  String(value).replace(/\b([a-z])/g, (match) => match.toUpperCase());

const normalizeIndustryValues = (value) => {
  if (!value) return [];
  return [...new Set(
    String(value)
      .split(/[,|]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => (item === item.toLowerCase() ? toTitleCaseWords(item) : item)),
  )];
};

const TIER_KEYS = ["Diamond", "Tier 1", "Tier 2", "Tier 3"];

const getTierLabel = (tier) => {
  if (tier == null || tier === "") return "";
  const s = String(tier).trim();
  if (TIER_KEYS.includes(s)) return s;
  const n = Number(tier);
  if (n === 1) return "Diamond";
  if (n === 2) return "Tier 1";
  if (n === 3) return "Tier 2";
  if (n === 4) return "Tier 3";
  return s || `Tier ${tier}`;
};

const TIER_CONFIG = {
  Diamond: { Icon: Diamond, classSuffix: "diamond" },
  "Tier 1": { Icon: LooksOne, classSuffix: "1" },
  "Tier 2": { Icon: LooksTwo, classSuffix: "2" },
  "Tier 3": { Icon: Looks3, classSuffix: "3" },
  1: { Icon: Diamond, classSuffix: "diamond" },
  2: { Icon: LooksOne, classSuffix: "1" },
  3: { Icon: LooksTwo, classSuffix: "2" },
  4: { Icon: Looks3, classSuffix: "3" },
};

function getTierConfig(tier) {
  if (tier == null || tier === "") return null;
  const label = getTierLabel(tier);
  return TIER_CONFIG[label] ?? TIER_CONFIG[tier] ?? TIER_CONFIG[Number(tier)] ?? null;
}

const InvestorProfile = ({
  data = {},
  onAdd,
  preferredIndustryPreview,
  isFlorenceLargeViewport,
}) => {
  const [fullProfilePreview, setfullProfilePreview] = useState(false);
  const tagsList = normalizeTags(data?.tags);
  const industry = normalizeIndustryValues(data?.user_preferred_industry_segmentation).join(", ");
  const tierCfg = getTierConfig(data?.tier);
  const tierLabel = getTierLabel(data?.tier);
  const TierIcon = tierCfg?.Icon;
  const isPrivateEquity = data?.type !== "Corporation";
  const companyName = replaceValueInDataSet(data.company_name);
  const investorLogoSrc = data?.url
    ? `${INVESTOR_LOGO_HOST}${data.url}?token=${LOGO_DEV_PUBLIC_KEY}`
    : IMAGE_HOST + DEFAULT_COMPANY_LOGO;

  const handleProfilePreview = () => {
    setfullProfilePreview(!fullProfilePreview);
  };

  const tagsBlock = (
    <div className="investor-profile-tags-wrap">
      {tagsList.length === 0 ? (
        <span className="investor-profile-tags-empty">—</span>
      ) : (
        tagsList.map((tag, i) => (
          <Tag key={i} color="blue" className="investor-profile-tag">
            {tag}
          </Tag>
        ))
      )}
    </div>
  );
  const websiteValue = replaceValueInDataSet(data.url);
  const typeValue = replaceValueInDataSet(data.type);
  const locationValue =
    [data?.us_state_full, data?.country].filter(Boolean).join(", ") || "-";

  return (
    <React.Fragment>
      <Card bordered={false} className="investor-profile-card">
        <div className="investor-profile-summary investor-profile-summary-plain">
          <div className="investor-profile-summary-head">
            <div className="investor-profile-summary-title">
              <div className="investor-profile-company-block">
                {TierIcon ? (
                  <span
                    className={`investor-profile-tier-icon-wrap tier-${tierCfg?.classSuffix ?? "none"}`}
                    title={getTierLabel(data?.tier)}
                  >
                    <TierIcon className="investor-profile-tier-icon" fontSize="inherit" />
                  </span>
                ) : null}
                <span
                  className={`investor-profile-company-logo tier-${tierCfg?.classSuffix ?? "none"}`}
                >
                  <Image
                    preview={false}
                    src={investorLogoSrc}
                    fallback={IMAGE_HOST + DEFAULT_COMPANY_LOGO}
                    alt={companyName === "-" ? "Investor logo" : companyName}
                  />
                </span>
                <h3>{companyName}</h3>
              </div>
              {tierCfg && tierLabel ? (
                <span
                  className={`investor-profile-tier-badge tier-${tierCfg?.classSuffix ?? "none"}`}
                  title={tierLabel}
                >
                  {tierLabel}
                </span>
              ) : null}
            </div>
          </div>
          <div className="investor-profile-meta-row">
            {replaceValueInDataSet(data.url) !== "-" ? (
              <a
                href={addHttpsPrefix(data.url)}
                target="_blank"
                rel="noreferrer"
                referrerPolicy="no-referrer"
                className="investor-profile-website-link"
              >
                <OpenInNew fontSize="inherit" />
                <span>Website</span>
              </a>
            ) : null}
            {replaceValueInDataSet(data.type) !== "-" ? (
              <div className="investor-profile-type-badge">
                {replaceValueInDataSet(data.type)}
              </div>
            ) : null}
            {data?.rank ? (
              <div className="investor-profile-rank-badge">
                <span>LLM Rank</span>
                <strong>{data.rank}</strong>
              </div>
            ) : null}
          </div>
          {tagsBlock}
        </div>
        <Row gutter={[16, 6]}>
          <Col span={preferredIndustryPreview ? 12 : 24}>
            <div className="investor-profile-container">
              <span>Location</span>
              <div className="investor-profile-content-multiline single-line">
                {[data?.us_state_full, data?.country].filter(Boolean).join(", ") || "-"}
              </div>
            </div>
          </Col>
          {preferredIndustryPreview && (
            <Col span={12}>
              <div className="investor-profile-container">
                <span>User Preferred Industry</span>
                <div className="investor-profile-content-multiline single-line">
                  {replaceValueInDataSet(industry)}
                </div>
              </div>
            </Col>
          )}
          <Col span={24}>
            <div className="investor-profile-container">
              <span>Description</span>
              <div className="investor-profile-content-multiline multi-line">
                <span>{replaceValueInDataSet(data.description)}</span>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="investor-profile-container">
              <span>{isPrivateEquity ? "Specialty Interests" : "Broad Specialty"}</span>
              <div className="investor-profile-content-multiline single-line">
                {replaceValueInDataSet(
                  isPrivateEquity ? data.specialty_interests : data.broad_specialty,
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="investor-profile-container">
              <span>{isPrivateEquity ? "Attribute Interests" : "Narrow Specialty"}</span>
              <div className="investor-profile-content-multiline single-line">
                {replaceValueInDataSet(
                  isPrivateEquity ? data.attribute_interests : data.narrow_specialty,
                )}
              </div>
            </div>
          </Col>
          {isPrivateEquity ? (
            <>
              <Col span={12}>
                <div className="investor-profile-container">
                  <span>Preferred EBITDA (in Millions)</span>
                  <div className="investor-profile-content-multiline single-line">
                    {replaceValueInDataSet(data.preferred_ebitda)}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="investor-profile-container">
                  <span>Preferred EV (in Millions)</span>
                  <div className="investor-profile-content-multiline single-line">
                    {replaceValueInDataSet(data.preferred_ev)}
                  </div>
                </div>
              </Col>
              {isFlorenceLargeViewport && (
                <>
                  <Col span={12}>
                    <div className="investor-profile-container">
                      <span>Preferred Revenue (in Millions)</span>
                      <div className="investor-profile-content-multiline single-line">
                        {replaceValueInDataSet(data.preferred_revenue)}
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="investor-profile-container">
                      <span>Typical Equity Investment (in Millions)</span>
                      <div className="investor-profile-content-multiline single-line">
                        {replaceValueInDataSet(data.typical_equity_investments)}
                      </div>
                    </div>
                  </Col>
                </>
              )}
            </>
          ) : (
            <Col span={24}>
              <div className="investor-profile-container">
                <span>NAICS Industry</span>
                <div className="investor-profile-content-multiline single-line">
                  {replaceValueInDataSet(data.naics_six_digit_industry)}
                </div>
              </div>
            </Col>
          )}
          {isFlorenceLargeViewport && !isPrivateEquity && (
            <>
              <Col span={12}>
                <div className="investor-profile-container">
                  <span>Products</span>
                  <div className="investor-profile-content-multiline single-line">
                    {replaceValueInDataSet(data.products)}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="investor-profile-container">
                  <span>Services</span>
                  <div className="investor-profile-content-multiline single-line">
                    {replaceValueInDataSet(data.services)}
                  </div>
                </div>
              </Col>
            </>
          )}
        </Row>
        <div className="investor-profile-card-footer">
          <Button
            onClick={handleProfilePreview}
            variant="contained"
            size="large"
            className="investor-profile-full-btn"
          >
            Full Profile
          </Button>
          <button
            className="add-investor-to-lead add-investor-to-lead-inline"
            onClick={onAdd}
            disabled={!data?.company_name}
          >
            Add to Leads
          </button>
        </div>
      </Card>
      <Modal
        className="investor-profile-modal"
        visible={fullProfilePreview}
        onCancel={handleProfilePreview}
        width={1200}
        footer={null}
        closable={false}
      >
        <button className="close-btn" onClick={handleProfilePreview}>
          <Close />
        </button>
        <div className="investor-profile-modal-title-wrap">
          <h2 className="investor-profile-modal-title">
            <span className="investor-profile-company-block">
              {TierIcon ? (
                <span
                  className={`investor-profile-tier-icon-wrap tier-${tierCfg?.classSuffix ?? "none"}`}
                  title={getTierLabel(data?.tier)}
                >
                  <TierIcon className="investor-profile-tier-icon" fontSize="inherit" />
                </span>
              ) : null}
              <span
                className={`investor-profile-company-logo tier-${tierCfg?.classSuffix ?? "none"}`}
              >
                <Image
                  preview={false}
                  src={investorLogoSrc}
                  fallback={IMAGE_HOST + DEFAULT_COMPANY_LOGO}
                  alt={companyName === "-" ? "Investor logo" : companyName}
                />
              </span>
              <span>{companyName}</span>
            </span>
            {tierCfg && tierLabel ? (
              <span
                className={`investor-profile-tier-badge investor-profile-tier-badge-modal tier-${tierCfg?.classSuffix ?? "none"}`}
                title={tierLabel}
              >
                {tierLabel}
              </span>
            ) : null}
          </h2>
        </div>
        <div className="investor-profile-meta-row investor-profile-meta-row-modal">
          {websiteValue !== "-" ? (
            <a
              href={addHttpsPrefix(data.url)}
              target="_blank"
              rel="noreferrer"
              referrerPolicy="no-referrer"
              className="investor-profile-website-link"
            >
              <OpenInNew fontSize="inherit" />
              <span>Website</span>
            </a>
          ) : null}
          {typeValue !== "-" ? (
            <div className="investor-profile-type-badge">{typeValue}</div>
          ) : null}
          {data?.rank ? (
            <div className="investor-profile-rank-badge">
              <span>LLM Rank</span>
              <strong>{data.rank}</strong>
            </div>
          ) : null}
        </div>
        <div className="investor-profile-modal-tags">
          <div className="investor-profile-tags-label">Tags</div>
          {tagsBlock}
        </div>

        <div className="investor-profile-modal-grid">
          <div className="investor-profile-modal-col">
            <div className="investor-profile-modal-section">
              <h3>Overview</h3>
              <div className="investor-profile-modal-fields two-col">
                <div className="investor-profile-container">
                  <span>Type</span>
                  <div className="investor-profile-content-multiline">{typeValue}</div>
                </div>
                <div className="investor-profile-container">
                  <span>URL</span>
                  {websiteValue === "-" ? (
                    <div className="investor-profile-content-multiline">
                      {websiteValue}
                    </div>
                  ) : (
                    <a
                      href={addHttpsPrefix(data.url)}
                      target="_blank"
                      rel="noreferrer"
                      referrerPolicy="no-referrer"
                      className="investor-profile-content-multiline investor-profile-link-block"
                    >
                      {websiteValue}
                    </a>
                  )}
                </div>
                <div className="investor-profile-container">
                  <span>Location</span>
                  <div className="investor-profile-content-multiline">
                    {locationValue}
                  </div>
                </div>
                <div className="investor-profile-container">
                  <span>Headquarter Address</span>
                  <div className="investor-profile-content-multiline">
                    {replaceValueInDataSet(data.hq_address)}
                  </div>
                </div>
                <div className="investor-profile-container full">
                  <span>Description</span>
                  <div className="investor-profile-content-multiline">
                    {replaceValueInDataSet(data.description)}
                  </div>
                </div>
                {!isPrivateEquity && (
                  <>
                    <div className="investor-profile-container">
                      <span>Broad Specialty</span>
                      <div className="investor-profile-content-multiline">
                        {replaceValueInDataSet(data.broad_specialty)}
                      </div>
                    </div>
                    <div className="investor-profile-container">
                      <span>Narrow Specialty</span>
                      <div className="investor-profile-content-multiline">
                        {replaceValueInDataSet(data.narrow_specialty)}
                      </div>
                    </div>
                    <div className="investor-profile-container full">
                      <span>NAICS Industry</span>
                      <div className="investor-profile-content-multiline">
                        {replaceValueInDataSet(data.naics_six_digit_industry)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {isPrivateEquity && (
              <div className="investor-profile-modal-section investor-profile-modal-section-last">
                <h3>Investment Focus</h3>
                <div className="investor-profile-modal-fields two-col">
                  <div className="investor-profile-container">
                    <span>Specialty Interests</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.specialty_interests)}
                    </div>
                  </div>
                  <div className="investor-profile-container">
                    <span>Attribute Interests</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.attribute_interests)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="investor-profile-modal-col">
            {isPrivateEquity && (
              <div className="investor-profile-modal-section">
                <h3>Financial Preferences</h3>
                <div className="investor-profile-modal-fields two-col">
                  <div className="investor-profile-container">
                    <span>Preferred EBITDA (in Millions)</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.preferred_ebitda)}
                    </div>
                  </div>
                  <div className="investor-profile-container">
                    <span>Preferred Revenue (in Millions)</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.preferred_revenue)}
                    </div>
                  </div>
                  <div className="investor-profile-container">
                    <span>Preferred EV (in Millions)</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.preferred_ev)}
                    </div>
                  </div>
                  <div className="investor-profile-container">
                    <span>Typical Equity Investment (in Millions)</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.typical_equity_investments)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="investor-profile-modal-section investor-profile-modal-section-last">
              <h3>Additional Details</h3>
              <div className="investor-profile-modal-fields two-col">
                {isPrivateEquity ? (
                  <div className="investor-profile-container">
                    <span>Sector Focus</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(data.sector_focus)}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="investor-profile-container full">
                      <span>Products</span>
                      <div className="investor-profile-content-multiline">
                        {replaceValueInDataSet(data.products)}
                      </div>
                    </div>
                    <div className="investor-profile-container full">
                      <span>Services</span>
                      <div className="investor-profile-content-multiline">
                        {replaceValueInDataSet(data.services)}
                      </div>
                    </div>
                  </>
                )}
                {preferredIndustryPreview && (
                  <div className="investor-profile-container">
                    <span>User Preferred Industry</span>
                    <div className="investor-profile-content-multiline">
                      {replaceValueInDataSet(industry)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default InvestorProfile;
